// Cloud save sync and the guest → account upgrade (AUTH-BACKEND.md §1).
//
// Rules that shape this file:
//   * Guest-first. Sync is an add-on; a failure here never costs a local save.
//   * Newest wins on conflict, but the loser is kept so the player can restore
//     it — silently eating someone's farm is not an acceptable merge strategy.

import {
  FutureSaveVersionError,
  migrateSave,
  StaleSaveVersionError,
} from '../core/persistence';
import type { GameStateStore } from '../core/store';
import type { GameState } from '../core/types';
import { t } from '../i18n';
import type { NetResult, SupabaseClient } from './supabase';

/**
 * The part of the state that is allowed to leave the device, and nothing else.
 *
 * Two fields are held back, and neither is the game's business to publish:
 *   * `settings.parentPinHash` — a 4-digit PIN behind one unkeyed SHA-256 is
 *     10,000 candidates. Fine on the device it protects (parentCorner.ts),
 *     instantly reversible the moment it is in someone else's hands, and the
 *     Parent Corner's own rationale only holds while it stays local.
 *   * `weather.latitude` / `weather.longitude` — the schema's stated privacy
 *     invariant is "the only personal datum stored anywhere is the PARENT's
 *     email… no child … location" (supabase/migrations/0001_init.sql:12-13).
 *
 * The weather city is a name ("San Juan, PR") and stays: the game is still a
 * weather game. What leaves is never enough to place a child.
 */
export function toSyncPayload(state: GameState): GameState {
  const { parentPinHash: _drop, ...settings } = state.settings;
  // `latitude`/`longitude` are no longer part of `WeatherState` — this build
  // never writes them. A row written before that change can still carry them,
  // so they are dropped by name here instead of being trusted to be absent.
  const { latitude: _lat, longitude: _lon, ...weather } = state.weather as GameState['weather'] & {
    latitude?: number;
    longitude?: number;
  };
  return { ...state, settings, weather };
}

/** The single upload shape every write path uses, so no call site can forget. */
function serialiseForSync(state: GameState): string {
  return JSON.stringify(toSyncPayload(state));
}

/**
 * A save row uploaded before the Parent PIN hash was held back can still carry
 * one. Adopting it would make the cloud a place the PIN can come FROM as well
 * as go to, so a downloaded save never restores it: the gate belongs to the
 * device that set it, and this device keeps its own.
 */
function withoutDeviceSecrets(state: GameState): GameState {
  const { parentPinHash: _drop, ...settings } = state.settings;
  return { ...state, settings };
}

export type SyncOutcome =
  | { status: 'uploaded' }
  | { status: 'downloaded'; state: GameState }
  | { status: 'in_sync' }
  | { status: 'conflict'; local: GameState; remote: GameState; remoteNewer: boolean }
  /**
   * `remote_newer`: the periodic push found a cloud save newer than the local
   * one and stood down instead of overwriting it. This is the deterministic
   * newest-wins rule (PERSISTENCE-SAVES.md) applied BEFORE any write — the
   * fix for the blind-overwrite window flagged in AUDIT-2026-08 §MED.
   */
  | { status: 'skipped'; reason: 'not_configured' | 'guest' | 'offline' | 'error' | 'remote_newer' }
  /**
   * The cloud row is from a newer build than this device understands
   * (persistence.ts `migrateSave`). Refusing beats guessing at the missing
   * fields — that is how a two-device player loses a farm. `foundVersion` is
   * for a diagnostic message; there is nothing actionable to do but update.
   */
  | { status: 'refused'; reason: 'future_version'; foundVersion: number };

/** Parses a `saves.save_json` blob into a current-shape GameState, or says why not. */
function parseRemoteSave(
  json: string,
):
  | { ok: true; state: GameState }
  | { ok: false; reason: 'corrupt' }
  | { ok: false; reason: 'stale'; foundVersion: number }
  | { ok: false; reason: 'future_version'; foundVersion: number } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return { ok: false, reason: 'corrupt' };
  }
  try {
    return { ok: true, state: withoutDeviceSecrets(migrateSave(parsed)) };
  } catch (err) {
    if (err instanceof FutureSaveVersionError) {
      return { ok: false, reason: 'future_version', foundVersion: err.foundVersion };
    }
    // A pre-refocus row describes a game that no longer exists. It is never
    // downloaded — otherwise a device that just reset its own save would pull
    // the old farm straight back out of the cloud on the next sync.
    if (err instanceof StaleSaveVersionError) {
      return { ok: false, reason: 'stale', foundVersion: err.foundVersion };
    }
    return { ok: false, reason: 'corrupt' };
  }
}

/** Where the overwritten save is parked so a wrong merge stays undoable. */
const BACKUP_KEY = 'clayfarm.conflictBackup';

export function stashBackup(state: GameState): void {
  try {
    localStorage.setItem(BACKUP_KEY, JSON.stringify(state));
  } catch {
    // Out of storage: the sync still proceeds, just without an undo.
  }
}

export function readBackup(): GameState | null {
  try {
    const raw = localStorage.getItem(BACKUP_KEY);
    return raw ? (JSON.parse(raw) as GameState) : null;
  } catch {
    return null;
  }
}

export function clearBackup(): void {
  try {
    localStorage.removeItem(BACKUP_KEY);
  } catch {
    /* nothing to clear */
  }
}

function skipReason(result: NetResult<unknown>): SyncOutcome {
  if (!result.ok && result.error === 'not_configured') {
    return { status: 'skipped', reason: 'not_configured' };
  }
  if (!result.ok && result.error === 'unauthorized') return { status: 'skipped', reason: 'guest' };
  if (!result.ok && result.error === 'offline') return { status: 'skipped', reason: 'offline' };
  return { status: 'skipped', reason: 'error' };
}

/**
 * Reconciles local and remote. A farm that only exists in one place is simply
 * copied to the other; two real saves are a conflict the player decides.
 */
export async function syncSave(
  store: GameStateStore,
  client: SupabaseClient,
): Promise<SyncOutcome> {
  if (!client.configured) return { status: 'skipped', reason: 'not_configured' };
  if (!client.signedIn) return { status: 'skipped', reason: 'guest' };

  const remote = await client.fetchSave();
  if (!remote.ok) return skipReason(remote);

  const local = store.getRawState();

  if (!remote.data) {
    const upload = await client.upsertSave(serialiseForSync(local));
    return upload.ok ? { status: 'uploaded' } : skipReason(upload);
  }

  const parsed = parseRemoteSave(remote.data.save_json);
  if (!parsed.ok) {
    if (parsed.reason === 'future_version') {
      // A newer build wrote this row. Do NOT overwrite it with the older
      // local save — that would destroy the other device's progress.
      return { status: 'refused', reason: 'future_version', foundVersion: parsed.foundVersion };
    }
    // Corrupt or pre-refocus row: local is the better truth, so overwrite it.
    // For the stale case this is the whole point of the save bump — the old
    // farm is replaced by the new one, never the other way round.
    const upload = await client.upsertSave(serialiseForSync(local));
    return upload.ok ? { status: 'uploaded' } : skipReason(upload);
  }
  const remoteState = parsed.state;

  const remoteTime = remoteState.lastSavedTimestamp ?? 0;
  const localTime = local.lastSavedTimestamp ?? 0;
  if (remoteTime === localTime) return { status: 'in_sync' };

  // A farm with nothing in it is not worth a conflict prompt.
  if (isUntouched(local)) {
    return { status: 'downloaded', state: remoteState };
  }
  if (isUntouched(remoteState)) {
    const upload = await client.upsertSave(serialiseForSync(local));
    return upload.ok ? { status: 'uploaded' } : skipReason(upload);
  }

  return {
    status: 'conflict',
    local,
    remote: remoteState,
    remoteNewer: remoteTime > localTime,
  };
}

/** A save nobody has really played: still in the tutorial, no eggs collected. */
export function isUntouched(state: GameState): boolean {
  return (
    !state.progression.tutorialComplete &&
    state.progression.eggsCollected === 0 &&
    state.progression.badges.length === 0
  );
}

/** Applies the player's conflict decision, keeping the loser recoverable. */
export async function resolveConflict(
  store: GameStateStore,
  client: SupabaseClient,
  choice: 'keep_local' | 'keep_remote',
  remoteState: GameState,
): Promise<SyncOutcome> {
  if (choice === 'keep_local') {
    stashBackup(remoteState);
    const upload = await client.upsertSave(serialiseForSync(store.getRawState()));
    return upload.ok ? { status: 'uploaded' } : skipReason(upload);
  }
  stashBackup(store.getRawState());
  // The remote save is a farm, not this device's settings: keep the PIN hash
  // this device set. A cloud row that carried one is stripped on read, so
  // replacing state wholesale would silently unlock the Parent Corner here.
  const localPinHash = store.getRawState().settings.parentPinHash;
  const merged: GameState = localPinHash
    ? { ...remoteState, settings: { ...remoteState.settings, parentPinHash: localPinHash } }
    : remoteState;
  store.replace(merged);
  return { status: 'downloaded', state: merged };
}

/**
 * First login on a device that has been played as a guest: the local farm is
 * the one the kid has been raising, so it goes up. If the parent's account
 * already has a save from another device, AUTH-BACKEND.md §1 calls for
 * "newest-wins with manual restore option" — so, unlike the periodic
 * `syncSave` used by the account panel's "sync now" (which still prompts,
 * since that is an ongoing multi-device session the player is actively in),
 * this resolves automatically: whichever save is newer stays loaded/synced,
 * and the other is stashed via `stashBackup` so it can be restored by hand.
 */
export async function upgradeGuestSave(
  store: GameStateStore,
  client: SupabaseClient,
): Promise<SyncOutcome> {
  if (!client.signedIn) return { status: 'skipped', reason: 'guest' };
  const remote = await client.fetchSave();
  if (!remote.ok) return skipReason(remote);
  if (!remote.data) {
    const upload = await client.upsertSave(serialiseForSync(store.getRawState()));
    return upload.ok ? { status: 'uploaded' } : skipReason(upload);
  }

  const parsed = parseRemoteSave(remote.data.save_json);
  if (!parsed.ok) {
    if (parsed.reason === 'future_version') {
      return { status: 'refused', reason: 'future_version', foundVersion: parsed.foundVersion };
    }
    // Corrupt row: the guest's local farm is the better truth.
    const upload = await client.upsertSave(serialiseForSync(store.getRawState()));
    return upload.ok ? { status: 'uploaded' } : skipReason(upload);
  }
  const remoteState = parsed.state;
  const local = store.getRawState();

  const remoteTime = remoteState.lastSavedTimestamp ?? 0;
  const localTime = local.lastSavedTimestamp ?? 0;
  if (remoteTime === localTime) return { status: 'in_sync' };
  if (isUntouched(local)) return resolveConflict(store, client, 'keep_remote', remoteState);
  if (isUntouched(remoteState)) return resolveConflict(store, client, 'keep_local', remoteState);

  const choice = remoteTime > localTime ? 'keep_remote' : 'keep_local';
  return resolveConflict(store, client, choice, remoteState);
}

/** Debounced background push, so a chatty autosave doesn't hammer the API. */
export class CloudAutosync {
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private store: GameStateStore,
    private client: SupabaseClient,
    private intervalMs = 5 * 60 * 1000,
    private onStatus?: (message: string) => void,
  ) {}

  start(): void {
    if (!this.client.configured || !this.client.signedIn) return;
    this.stop();
    this.timer = setInterval(() => void this.push(), this.intervalMs);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  /**
   * Periodic background push. Unlike a manual "sync now", this never prompts —
   * so it must also never destroy: the remote row is fetched and compared
   * BEFORE any write, and a newer cloud save simply wins (newest-updatedAt,
   * i.e. `lastSavedTimestamp`, per PERSISTENCE-SAVES.md). Only these push:
   *   * no cloud row at all            → local uploads
   *   * cloud row corrupt              → local overwrites (local is readable truth)
   *   * local strictly newer           → local uploads
   * Equal timestamps are treated as already-synced (same clock write) and the
   * future-version refusal applies here exactly as it does on pull.
   */
  async push(): Promise<SyncOutcome> {
    if (!this.client.configured) return { status: 'skipped', reason: 'not_configured' };
    if (!this.client.signedIn) return { status: 'skipped', reason: 'guest' };

    const local = this.store.getRawState();
    const remote = await this.client.fetchSave();
    if (!remote.ok) return skipReason(remote);

    if (remote.data) {
      const parsed = parseRemoteSave(remote.data.save_json);
      if (!parsed.ok) {
        if (parsed.reason === 'future_version') {
          // A newer build wrote the cloud row; pushing would clobber it.
          return { status: 'refused', reason: 'future_version', foundVersion: parsed.foundVersion };
        }
        // Corrupt row: fall through and overwrite with the readable local save.
      } else {
        const remoteTime = parsed.state.lastSavedTimestamp ?? 0;
        const localTime = local.lastSavedTimestamp ?? 0;
        // The stale-local guard: never overwrite a newer farm with an older one.
        if (remoteTime > localTime) return { status: 'skipped', reason: 'remote_newer' };
        if (remoteTime === localTime) return { status: 'in_sync' };
      }
    }

    const result = await this.client.upsertSave(serialiseForSync(local));
    if (!result.ok) {
      // Quota, network, or server failure: the local save is untouched, tell
      // the player once, calmly. Never throw, never wipe.
      this.onStatus?.(t('sync.failed'));
      return skipReason(result);
    }
    return { status: 'uploaded' };
  }
}
